import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  UploadCloud,
} from '@/components/icons';
import { WifiSlash } from '@/components/icons/WifiSlash';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useEvidenceSyncStatus } from '@/lib/offline/use-evidence-sync';

/**
 * `EvidenceSyncIndicator` (W45-F5-S1, #1476) — topbar control surfacing the
 * offline evidence queue.
 *
 * - Hidden entirely when the queue is empty and the browser is online (zero
 *   visual weight in the normal case, like the connection banner).
 * - Shows a pending count badge + a state icon (offline / syncing / failed /
 *   pending).
 * - The dropdown lists each pending capture with its status and a manual
 *   "retry" for failed items, plus a "sync now" action.
 *
 * Data comes from `useEvidenceSyncStatus`, which subscribes to the
 * framework-agnostic sync worker.
 */
export function EvidenceSyncIndicator(): JSX.Element | null {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const { pending, syncing, failed, online, items, retry, syncNow } =
    useEvidenceSyncStatus();

  // Nothing queued and we're online → no chrome at all.
  if (pending === 0 && online) return null;

  const state: 'offline' | 'syncing' | 'failed' | 'pending' = !online
    ? 'offline'
    : syncing
      ? 'syncing'
      : failed > 0
        ? 'failed'
        : 'pending';

  const StateIcon =
    state === 'offline'
      ? WifiSlash
      : state === 'syncing'
        ? Loader2
        : state === 'failed'
          ? AlertCircle
          : UploadCloud;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('offline.evidence.indicator.ariaLabel', {
            count: pending,
          })}
          data-testid="evidence-sync-indicator"
        >
          <StateIcon
            className={cn('h-5 w-5', state === 'syncing' && 'animate-spin')}
            aria-hidden="true"
          />
          {pending > 0 ? (
            <span
              data-testid="evidence-sync-badge"
              className={cn(
                'absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white',
                state === 'failed' ? 'bg-rose-500' : 'bg-amber-500',
              )}
            >
              {pending > 99 ? '99+' : pending}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <p className="text-sm font-semibold">
            {t('offline.evidence.indicator.title')}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void syncNow()}
            disabled={!online || syncing}
            data-testid="evidence-sync-now"
          >
            <RefreshCw
              className={cn('h-4 w-4', syncing && 'animate-spin')}
              aria-hidden="true"
            />
            {t('offline.evidence.indicator.syncNow')}
          </Button>
        </div>

        <p
          className="px-3 py-1.5 text-xs text-muted-foreground"
          role="status"
          data-testid="evidence-sync-summary"
        >
          {!online
            ? t('offline.evidence.indicator.offlineHint', { count: pending })
            : syncing
              ? t('offline.evidence.indicator.syncing', { count: pending })
              : t('offline.evidence.indicator.pending', { count: pending })}
        </p>

        <ul className="max-h-72 overflow-y-auto py-1" data-testid="evidence-sync-list">
          {items.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">
              {t('offline.evidence.indicator.empty')}
            </li>
          ) : (
            items.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-2 px-3 py-2 text-sm"
                data-testid="evidence-sync-item"
              >
                <ItemStatusIcon status={item.status} />
                <span className="min-w-0 flex-1 truncate">
                  {item.note ||
                    item.filenames[0] ||
                    t('offline.evidence.indicator.untitled')}
                </span>
                {item.status === 'failed' ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void retry(item.id)}
                    disabled={!online}
                    data-testid="evidence-sync-retry"
                  >
                    {t('offline.evidence.indicator.retry')}
                  </Button>
                ) : null}
              </li>
            ))
          )}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ItemStatusIcon({
  status,
}: {
  status: 'pending' | 'syncing' | 'synced' | 'failed';
}): JSX.Element {
  if (status === 'syncing') {
    return (
      <Loader2
        className="h-4 w-4 shrink-0 animate-spin text-muted-foreground"
        aria-hidden="true"
      />
    );
  }
  if (status === 'failed') {
    return (
      <AlertCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
    );
  }
  if (status === 'synced') {
    return (
      <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
    );
  }
  return (
    <UploadCloud
      className="h-4 w-4 shrink-0 text-muted-foreground"
      aria-hidden="true"
    />
  );
}

export default EvidenceSyncIndicator;
